import { useMutation } from '@apollo/client';
import { Button, Callout, TextField } from '@radix-ui/themes';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { FORGOT_PASSWORD, RESEND_INVITATION } from '../../../gql';
import Title from '../../elements/Title';
import BackToHubert from '../../navbar/BackToHubert';

const Forgot = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [validated, setValidated] = useState(false);

  const [email, setEmail] = useState('');
  const [loginError, setLoginError] = useState<string>();
  const [sent, setSent] = useState(false);
  const [notActivated, setNotActivated] = useState(false);

  const [ForgotPassword, { data, loading }] = useMutation(FORGOT_PASSWORD, {
    notifyOnNetworkStatusChange: true,
  });

  const [ResendInvitation, { loading: ResendInvitationLoading }] = useMutation(RESEND_INVITATION, {
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!location.state) return;

    const state = location.state.state;
    const email = location.state.email;

    setValidated(true);

    if (state === 'not-activated' && email) {
      setNotActivated(true);
      setEmail(email);
    }
  }, []);

  useEffect(() => {
    if (data && data.ForgotPasswordMagicLink) {
      switch (data.ForgotPasswordMagicLink.__typename) {
        case 'User': {
          if (data.ForgotPasswordMagicLink.email === email) {
            setSent(true);
            setTimeout(() => {
              navigate('/');
            }, 3000);
          } else {
            setSent(false);
          }
          break;
        }

        case 'UserNotActivated': {
          setNotActivated(true);
          break;
        }

        case 'LoginError': {
          console.info('LoginError', { ...data });
          const code = data.ForgotPasswordMagicLink.code;
          setLoginError(t(`forgot.errors.${code}`, { defaultValue: t('forgot.errors.generic') }));
          break;
        }

        default: {
          break;
        }
      }
    }
  }, [data, email]);

  const handleSubmit = () => {
    if (!notActivated) {
      ForgotPassword({
        variables: {
          email: email.trim().toLowerCase(),
        },
      })
        .then((response) => {
          console.log('Sent', response);
        })
        .catch((err) => {
          console.error(err);
          setLoginError(t('forgot.accountNotFound', { email }));
        });
    } else {
      ResendInvitation({
        variables: {
          email: email.trim().toLowerCase(),
        },
      })
        .then((response) => {
          if (response.data?.ReinviteToOrganization) {
            setSent(true);
            setTimeout(() => {
              navigate('/');
            }, 4000);
          } else {
            setLoginError(t('forgot.errors.generic'));
          }
        })
        .catch(() => {
          setLoginError(t('forgot.errors.generic'));
        });
    }
  };

  const checkEmail = (event: ChangeEvent<HTMLInputElement>) => {
    setValidated(event.target.validity.valid);
    setEmail(event.target.value);
  };

  return (
    <div className="vh-100 flex items-center justify-center">
      <Title title={t('forgot.pageTitle')} />

      <BackToHubert />
      <div className="pb-12 w-3/4" style={{ maxWidth: '25rem' }}>
        <div className="text-center">
          <img src="/logo/hubert_logo_lockup_dark@2x.png" className="mw-100" alt={t('forgot.logo.alt')} />
          <h1 style={{ fontSize: '1.9rem' }}>{t('forgot.heading')}</h1>
        </div>
        {loginError && (
          <Callout.Root color="red">
            <Callout.Text className="mb-0">{loginError}</Callout.Text>
          </Callout.Root>
        )}
        {notActivated && (
          <Callout.Root color="blue">
            <Callout.Text className="mb-0">{t('forgot.notActivatedMessage')}</Callout.Text>
          </Callout.Root>
        )}
        <TextField.Root
          type="email"
          placeholder={t('forgot.emailPlaceholder')}
          size="3"
          className="my-6"
          required
          value={email}
          disabled={notActivated || loading}
          onChange={checkEmail}
        />

        <Button
          size="3"
          className="w-full"
          color={validated ? undefined : 'gray'}
          variant={validated ? 'solid' : 'soft'}
          disabled={loading || !validated || sent}
          loading={notActivated ? ResendInvitationLoading : loading}
          onClick={handleSubmit}
        >
          {sent
            ? t('forgot.button.sent')
            : notActivated
              ? t('forgot.button.resendActivation')
              : t('forgot.button.sendRecovery')}
        </Button>
      </div>
    </div>
  );
};

export default Forgot;
